import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Receipt, Building2, CheckCircle2, Clock, XCircle, Banknote, ScrollText, Save, Fingerprint, Eye, FileDown, LibraryBig, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import { useCurrency } from "@/lib/currency";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const INV_STATUS = {
  submitted: "text-nc-blue border-nc-blue/40 bg-nc-blue/10",
  approved: "text-nc-orange border-nc-orange/40 bg-nc-orange/10",
  paid: "text-nc-green border-nc-green/40 bg-nc-green/10",
  rejected: "text-red-400 border-red-500/40 bg-red-500/10",
};
const DOC_LABEL = { nda: "NDA", ip: "IP Agreement", contract: "Dev Contract" };

export default function AdminHiring() {
  const { fmt } = useCurrency();
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [identity, setIdentity] = useState([]);
  const [identityQuery, setIdentityQuery] = useState("");
  const [identityStatus, setIdentityStatus] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [resub, setResub] = useState(null);
  const [resubConfirm, setResubConfirm] = useState(false);
  const [devs, setDevs] = useState([]);
  const [company, setCompany] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutRefs, setPayoutRefs] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/admin/identity").then((r) => setIdentity(r.data)).catch(() => {});
    api.get("/admin/identity/resubmission-candidates").then((r) => setResub(r.data)).catch(() => setResub(null));
    api.get("/users").then((r) => setDevs(r.data.filter((u) => u.role === "dev" || u.role === "admin"))).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/market/payouts/admin").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);

  const searchIdentity = useCallback((q = identityQuery, status = identityStatus) => {
    api.get("/admin/identity", { params: { q, status } })
      .then((r) => setIdentity(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [identityQuery, identityStatus]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };


  const saveCompany = () => act(() => api.put("/company", company), "Company details saved");

  const pendingCount = invoices.filter((i) => i.status === "submitted").length;
  const unpaidTotal = invoices.filter((i) => i.status === "approved").reduce((s, i) => s + i.total, 0);

  return (
    <section data-testid="admin-hiring-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-nc-blue" /> Hiring & Payments
        </h2>
        <div className="flex gap-2 flex-wrap">
          {[["invoices", `Invoices (${pendingCount} new)`], ["identity", `Identity (${identity.filter((u) => u.id_status === "pending").length} pending)`], ["payouts", `Payouts (${payouts.filter((p) => p.status === "pending").length} pending)`], ["company", "Company Details"]].map(([t, label]) => (
            <button key={t} data-testid={`hiring-tab-${t}`} onClick={() => setTab(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors duration-300 ${tab === t ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 flex flex-col gap-4">
        {tab === "identity" && (
          <>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
              Review each worker's government-issued ID against their declared legal name, then verify or reject. Viewing a document is recorded in the audit log.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px_auto] gap-2 border border-white/10 bg-[#050505]/50 p-3">
              <input
                data-testid="identity-search-input"
                className={inputCls}
                value={identityQuery}
                onChange={(e) => setIdentityQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchIdentity()}
                placeholder="Search email, account name, or legal name"
              />
              <select data-testid="identity-status-filter" className={inputCls} value={identityStatus} onChange={(e) => { const next = e.target.value; setIdentityStatus(next); searchIdentity(identityQuery, next); }}>
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
              <button type="button" data-testid="identity-search-button" onClick={() => searchIdentity()} className="font-mono text-[9px] tracking-[0.18em] uppercase px-4 py-2 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors">
                Find ID
              </button>
            </div>
            <p data-testid="identity-result-count" className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-600">
              {identity.length} identity record{identity.length === 1 ? "" : "s"} shown // pending first
            </p>
            {resub && (
              <div data-testid="identity-resubmit-panel" className={`border p-4 flex items-center justify-between gap-4 flex-wrap ${resub.count > 0 ? "border-nc-orange/30 bg-nc-orange/5" : "border-white/10 bg-[#050505]/50"}`}>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 flex items-center gap-2">
                    <ShieldAlert className={`w-3.5 h-3.5 ${resub.count > 0 ? "text-nc-orange" : "text-nc-green"}`} />
                    <span data-testid="identity-resubmit-count">{resub.count} dev{resub.count === 1 ? "" : "s"} without an ID document on file{resub.stale_verified > 0 ? ` — ${resub.stale_verified} stale verified flag${resub.stale_verified === 1 ? "" : "s"}` : ""}</span>
                  </p>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1 leading-relaxed">
                    Emails every affected dev asking them to resubmit their ID. Stale "verified" flags without a document are cleared immediately, so they cannot sign contracts until re-verified.
                  </p>
                </div>
                {resub.count > 0 && (
                  resubConfirm ? (
                    <div className="flex gap-2 shrink-0">
                      <button data-testid="identity-resubmit-confirm" disabled={busy} onClick={() => act(() => api.post("/admin/identity/request-resubmissions").then((r) => { toast.success(r.data.message); setResubConfirm(false); }), null)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-nc-orange hover:bg-nc-orange/80 text-white transition-colors disabled:opacity-40">
                        Confirm — email {resub.count} dev{resub.count === 1 ? "" : "s"}
                      </button>
                      <button data-testid="identity-resubmit-cancel" onClick={() => setResubConfirm(false)} className="font-mono text-[9px] uppercase px-3 py-2 text-zinc-400">Cancel</button>
                    </div>
                  ) : (
                    <button data-testid="identity-resubmit-send" onClick={() => setResubConfirm(true)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5 shrink-0">
                      <Fingerprint className="w-3.5 h-3.5" /> Request ID resubmissions
                    </button>
                  )
                )}
              </div>
            )}
            {identity.length === 0 && <p data-testid="identity-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No identity submissions match.</p>}
            {identity.map((u, i) => {
              const st = u.id_status;
              const badge = st === "verified" && u.submission_complete ? "text-nc-green border-nc-green/40 bg-nc-green/10" : st === "verified" ? "text-red-300 border-red-500/40 bg-red-500/10" : st === "pending" ? "text-nc-orange border-nc-orange/40 bg-nc-orange/10" : st === "rejected" ? "text-red-400 border-red-500/40 bg-red-500/10" : "text-zinc-400 border-white/15";
              return (
                <div key={u.id} data-testid={`identity-card-${i + 1}`} className="border border-white/10 bg-[#050505]/60 p-5 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-sans font-bold text-sm text-zinc-100">{u.legal_name || u.name || "No legal name submitted"}</p>
                      <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${badge}`}>{u.id_status || "not submitted"}</span>
                      {!u.submission_complete && <span data-testid={`identity-integrity-warning-${i + 1}`} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-red-400/50 bg-red-500/10 text-red-300 px-2 py-0.5">No reviewable ID file</span>}
                    </div>
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">
                      {u.email} // {u.role} // {u.id_type || "id type missing"} // submitted {u.id_submitted_at ? String(u.id_submitted_at).slice(0, 10) : "never"}
                    </p>
                    <p className={`font-mono text-[10px] mt-2 ${u.contract_ready ? "text-nc-green" : "text-nc-orange"}`}>
                      Contracts: {u.signed_contracts + u.legacy_signed_documents} signed · {u.pending_contracts} pending // {u.contract_ready ? "identity verification backs signatures" : "request a fresh ID before relying on signatures"}
                    </p>
                    {st === "rejected" && u.id_reject_reason && (
                      <p className="font-mono text-[10px] text-red-400 mt-1">Reason: {u.id_reject_reason}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <button
                      data-testid={`identity-view-${i + 1}`}
                      disabled={!u.has_document}
                      onClick={async () => {
                        const token = localStorage.getItem("nc_token");
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/identity/${u.id}/document`, { headers: { Authorization: `Bearer ${token}` } });
                        if (res.ok) {
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } else {
                          toast.error("Could not load document");
                        }
                      }}
                      className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5 disabled:opacity-35 disabled:hover:bg-transparent"
                    >
                      <Eye className="w-3.5 h-3.5" /> View ID
                    </button>
                    {st !== "verified" && (
                      <button data-testid={`identity-verify-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/identity/${u.id}/review`, { action: "verify" }), "Identity verified")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-nc-green hover:bg-nc-green/80 text-white transition-colors flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Verify
                      </button>
                    )}
                    {st === "verified" && !u.submission_complete ? (
                      <button data-testid={`identity-unverify-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/identity/${u.id}/review`, { action: "unverify" }), "Stale verified flag cleared — ask for a fresh ID")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                        <ShieldAlert className="w-3.5 h-3.5" /> Clear stale flag
                      </button>
                    ) : st !== "rejected" && (
                      <button data-testid={`identity-reject-${i + 1}`} disabled={busy} onClick={() => setRejectFor(rejectFor === u.id ? null : u.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    )}
                  </div>
                  {rejectFor === u.id && (
                    <div className="w-full border border-red-500/30 bg-red-500/5 p-4 flex flex-col gap-3">
                      <p className="font-mono text-[10px] tracking-[0.2em] text-red-400 uppercase">Rejection reason (shown to the worker)</p>
                      <input data-testid={`identity-reject-reason-${i + 1}`} className={inputCls} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Photo too blurry — please resubmit a clear image" />
                      <div className="flex gap-2">
                        <button data-testid={`identity-reject-confirm-${i + 1}`} disabled={busy || rejectReason.trim().length < 5} onClick={() => act(() => api.post(`/admin/identity/${u.id}/review`, { action: "reject", reason: rejectReason }).then(() => { setRejectFor(null); setRejectReason(""); }), "Identity rejected")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-40">
                          Confirm Reject
                        </button>
                        <button onClick={() => { setRejectFor(null); setRejectReason(""); }} className="font-mono text-[9px] uppercase px-3 py-2 text-zinc-400">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {tab === "invoices" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-white/10 bg-[#050505]/60 p-4">
                <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Awaiting review</p>
                <p data-testid="invoice-stat-pending" className="font-sans font-bold text-2xl text-nc-blue mt-1">{pendingCount}</p>
              </div>
              <div className="border border-white/10 bg-[#050505]/60 p-4">
                <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Approved, unpaid</p>
                <p data-testid="invoice-stat-unpaid" className="font-sans font-bold text-2xl text-nc-orange mt-1">{fmt(unpaidTotal)}</p>
              </div>
              <div className="border border-white/10 bg-[#050505]/60 p-4">
                <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase">Total invoices</p>
                <p className="font-sans font-bold text-2xl text-zinc-50 mt-1">{invoices.length}</p>
              </div>
            </div>
            {invoices.length === 0 && <p data-testid="invoices-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No invoices submitted yet.</p>}
            {invoices.map((inv, i) => (
              <div key={inv.id} data-testid={`admin-invoice-${i + 1}`} className="border border-white/10 bg-[#050505]/60 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-sans font-bold text-sm text-zinc-100">{inv.invoice_number}</p>
                      <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${INV_STATUS[inv.status]}`}>{inv.status}</span>
                      <p className="font-sans font-bold text-base text-nc-green">{fmt(inv.total)}</p>
                      {inv.display_currency && inv.display_currency !== "AUD" && inv.converted_total != null && (
                        <p className="font-mono text-[8px] text-nc-blue mt-0.5">≈ {inv.display_currency} {inv.converted_total.toLocaleString(undefined, { minimumFractionDigits: 2 })} indicative</p>
                      )}
                    </div>
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">{inv.user_name} // {inv.user_email} // {inv.created_at.slice(0, 10)} {inv.period ? `// ${inv.period}` : ""}</p>
                    <div className="mt-3 border border-white/10 bg-black/40">
                      {inv.line_items.map((li, j) => (
                        <div key={j} className="flex justify-between gap-4 px-3 py-1.5 border-b border-white/5 font-mono text-[11px]">
                          <span className="text-zinc-300 truncate">{li.description}</span>
                          <span className="text-zinc-500 shrink-0">{li.qty} {li.unit} × {li.rate.toFixed(2)} = <span className="text-zinc-200">{li.amount.toFixed(2)}</span></span>
                        </div>
                      ))}
                    </div>
                    <p className="font-mono text-[11px] text-zinc-400 mt-2 leading-relaxed">{inv.job_summary}</p>
                    {inv.proof_links && <p className="font-mono text-[10px] text-nc-blue mt-1">Proof: {inv.proof_links}</p>}
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">Pay via {inv.payment_method === "bank" ? "Bank" : inv.payment_method === "payoneer" ? "Payoneer" : "PayPal"}: {inv.payment_details}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {inv.status === "submitted" && (
                      <>
                        <button data-testid={`invoice-approve-${i + 1}`} disabled={busy} onClick={() => act(() => api.patch(`/admin/invoices/${inv.id}`, { status: "approved" }), "Invoice approved")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button data-testid={`invoice-reject-${i + 1}`} disabled={busy} onClick={() => act(() => api.patch(`/admin/invoices/${inv.id}`, { status: "rejected" }), "Invoice rejected")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-1.5">
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                    {inv.status === "approved" && (
                      <button data-testid={`invoice-pay-${i + 1}`} disabled={busy} onClick={() => act(() => api.patch(`/admin/invoices/${inv.id}`, { status: "paid" }), "Marked as paid")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-nc-green hover:bg-nc-green/80 text-white transition-colors flex items-center gap-1.5">
                        <Banknote className="w-3.5 h-3.5" /> Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {tab === "payouts" && (
          <>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
              Developer withdrawal requests from Command Center earnings. Process each via Payoneer, then mark paid with the transfer reference.
            </p>
            {payouts.length === 0 && <p data-testid="payouts-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No payout requests yet.</p>}
            {payouts.map((p, i) => (
              <div key={p.id} data-testid={`payout-card-${i + 1}`} className="border border-white/10 bg-[#050505]/60 p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-sans font-bold text-sm text-zinc-100">{p.dev_name} <span className="font-mono text-[10px] text-zinc-500">// {p.dev_email}</span></p>
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">{p.method} // requested {String(p.created_at).slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-sans font-bold text-lg text-nc-green">{fmt(p.amount)}</p>
                    <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-1 ${p.status === "paid" ? "text-nc-green border-nc-green/40 bg-nc-green/10" : p.status === "pending" ? "text-nc-orange border-nc-orange/40 bg-nc-orange/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>{p.status}</span>
                  </div>
                </div>
                {p.status === "pending" && (
                  <div className="flex gap-2 flex-wrap">
                    <input
                      data-testid={`payout-ref-${i + 1}`}
                      className="flex-1 min-w-[220px] bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-green"
                      placeholder="Payoneer transfer reference (optional)"
                      value={payoutRefs[p.id] || ""}
                      onChange={(e) => setPayoutRefs((r) => ({ ...r, [p.id]: e.target.value }))}
                    />
                    <button data-testid={`payout-paid-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/market/payouts/${p.id}/mark-paid`, { reference: payoutRefs[p.id] || "" }), "Payout marked paid")} className="bg-nc-green hover:bg-nc-green/80 text-white font-sans font-semibold uppercase tracking-[0.15em] text-[10px] px-4 py-2 transition-colors">
                      Mark Paid
                    </button>
                    <button data-testid={`payout-decline-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/market/payouts/${p.id}/decline`, { reason: payoutRefs[p.id] || "Declined by admin" }), "Payout declined")} className="border border-red-400/40 text-red-400 hover:bg-red-400/10 font-mono uppercase tracking-[0.15em] text-[10px] px-4 py-2 transition-colors">
                      Decline
                    </button>
                  </div>
                )}
                {p.status === "paid" && p.reference && <p className="font-mono text-[10px] text-zinc-500">Reference: {p.reference} // by {p.processed_by}</p>}
                {p.status === "declined" && <p className="font-mono text-[10px] text-red-400">Reason: {p.reason}</p>}
              </div>
            ))}
          </>
        )}

        {tab === "company" && company && (
          <div className="border border-white/10 bg-[#050505]/60 p-5 flex flex-col gap-4 max-w-xl">
            <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Registered Company Details</p>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">These appear on every contract and invoice the system generates.</p>
            <div>
              <label className={labelCls}>Company name</label>
              <input data-testid="company-name-input" className={inputCls} value={company.company_name} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>ABN (Australian Business Number)</label>
              <input data-testid="company-abn-input" className={inputCls} value={company.abn} onChange={(e) => setCompany((c) => ({ ...c, abn: e.target.value }))} placeholder="e.g. 12 345 678 901" />
            </div>
            <div>
              <label className={labelCls}>Registered address</label>
              <input data-testid="company-address-input" className={inputCls} value={company.address} onChange={(e) => setCompany((c) => ({ ...c, address: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Contact email</label>
              <input data-testid="company-email-input" className={inputCls} value={company.email} onChange={(e) => setCompany((c) => ({ ...c, email: e.target.value }))} />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="company-gst-toggle" type="checkbox" checked={company.gst_registered} onChange={(e) => setCompany((c) => ({ ...c, gst_registered: e.target.checked }))} className="accent-[#2E6BFF]" />
              <span className="font-mono text-[10px] text-zinc-300">Registered for GST (invoices will show 10% GST)</span>
            </label>
            <button data-testid="company-save" disabled={busy} onClick={saveCompany} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-2.5 flex items-center gap-2 transition-colors">
              <Save className="w-3.5 h-3.5" /> Save Details
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
